import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { axiosInstance } from '../../shared/api/axiosInstance';
import { UserProfileUpdateResponse, VerifyCodeResponse } from '../../shared/types/authTypes';
import {
    EmailPayload,
    EmailVerificationState,
    SendVerificationCodeResponse,
    VerifyCodePayload,
} from '../../shared/types/emailVertifyTypes';
import { setToken } from './authSlice';

export const sendVerificationCode = createAsyncThunk<
    SendVerificationCodeResponse,
    EmailPayload,
    { rejectValue: string }
>('emailVerification/sendCode', async ({ email }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/auth/email/resend-code', {
            data: {
                type: 'email-verification',
                attributes: { email },
            },
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(
                axiosError.response?.data.message || 'Failed to resend verification code',
            );
        }
        return rejectWithValue('An unexpected error occurred while resending the code');
    }
});

export const verifyEmailCode = createAsyncThunk<
    VerifyCodeResponse,
    VerifyCodePayload,
    { rejectValue: string }
>('emailVerification/verifyCode', async ({ email, code }, { rejectWithValue, dispatch }) => {
    try {
        const response = await axiosInstance.post('/auth/email/verify-code', {
            data: {
                type: 'email-verification',
                attributes: { email, code },
            },
        });

        const token = response.data.meta?.token;
        if (token) {
            dispatch(setToken(token));
        }

        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(
                axiosError.response?.data.message || 'Invalid or expired verification code',
            );
        }
        return rejectWithValue('An unexpected error occurred during verification');
    }
});

export const initialState: EmailVerificationState = {
    isLoading: false,
    resendLoading: false,
    error: null,
    fieldErrors: {},
    resendError: null,
    resendSuccess: null,
    success: false,
};

export const emailVerificationSlice = createSlice({
    name: 'emailVerification',
    initialState,
    reducers: {
        resetEmailVerificationState: (state) => {
            state.isLoading = false;
            state.error = null;
            state.success = false;
            state.resendError = '';
            state.error = '';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(sendVerificationCode.pending, (state) => {
                state.resendLoading = true;
                state.resendError = null;
                state.resendSuccess = null;
            })
            .addCase(sendVerificationCode.fulfilled, (state) => {
                state.resendLoading = false;
                state.resendSuccess = 'code sent!';
            })
            .addCase(sendVerificationCode.rejected, (state, action) => {
                state.resendLoading = false;
                state.resendError = action.payload || 'Failed to resend code';
            })
            .addCase(verifyEmailCode.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(verifyEmailCode.fulfilled, (state) => {
                state.isLoading = false;
                state.success = true;
            })
            .addCase(verifyEmailCode.rejected, (state, action) => {
                state.isLoading = false;
                state.success = false;
                state.error = action.payload || 'Verification failed';
            });
    },
});

export const { resetEmailVerificationState } = emailVerificationSlice.actions;
export default emailVerificationSlice.reducer;
